import type { ComponentProps, ReactNode } from "react";

import { cn } from "../../lib/cn";
import type { Tone } from "./tone";

/** A small chip before a notification's time that says what the notification is: "2 work items". */
export interface NotificationTag {
	readonly label: string;
	readonly tone: Tone;
}

// The feed row's bordered mini-chip, filled with a tone's soft colour and written in the tone itself; the neutral one
// takes the meta line's grey.
const tagClassNames: Record<Tone, string> = {
	neutral: "border-line bg-bg",
	ok: "border-ok/30 bg-ok-soft text-ok",
	warn: "border-warn/30 bg-warn-soft text-warn",
	alert: "border-alert/30 bg-alert-soft text-alert",
	info: "border-info/30 bg-info-soft text-info",
};

export interface NotificationRowProps extends ComponentProps<"a"> {
	/** Background class for the leading dot, e.g. a section color like `bg-legal`. */
	readonly dotClassName: string;
	readonly title: string;
	/** Two lines at most: a longer one is cut short here, and read whole where the notification is opened. */
	readonly description: string;
	readonly time: string;
	/** What the notification is, as a chip before the time; a plain notification has none. */
	readonly tag?: NotificationTag;
	/** The one button at the end of the row: Archive, or Restore on the archived list. */
	readonly action: ReactNode;
}

/**
 * One notification: dot · title · description · tag and time, with a single action at the end. The title is the link,
 * so the row is not clickable as a whole and the action is reachable on its own. Render it inside a <ul>; route it with
 * NotificationRowLink from src/components/links.ts.
 */
export function NotificationRow({
	dotClassName,
	title,
	description,
	time,
	tag,
	action,
	className,
	...props
}: NotificationRowProps) {
	return (
		<li className="flex items-start gap-2.5 border-b border-line px-4 py-3 transition-colors duration-100 last:border-0 hover:bg-hover-tint">
			<span aria-hidden="true" className={cn("mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full", dotClassName)} />
			<div className="min-w-0 flex-1">
				<a
					className={cn("mb-px block truncate text-ctl-lg font-medium text-ink hover:underline", className)}
					{...props}
				>
					{title}
				</a>
				<div className="line-clamp-2 text-caption leading-[1.45] text-ink-2">{description}</div>
				<div className="mt-[3px] flex items-center gap-2 text-label text-ink-3 tabular-nums">
					{tag === undefined ? null : (
						<span className={cn("rounded-[3px] border px-[5px] py-px", tagClassNames[tag.tone])}>
							{tag.label}
						</span>
					)}
					<span>{time}</span>
				</div>
			</div>
			{action}
		</li>
	);
}
